import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, LifeBuoy } from 'lucide-react';
import { Card } from '../components';
import { getIntro } from '../services/medewerkerApi';
import type { IntroSectie } from '../services/medewerkerApi';

const GELEZEN_KEY = 'medewerker_intro_gelezen';

function isGelezen(): boolean {
  try {
    return localStorage.getItem(GELEZEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markeerGelezen(): void {
  try {
    localStorage.setItem(GELEZEN_KEY, '1');
  } catch {
    /* localStorage niet beschikbaar — dan staat de intro de volgende keer weer open */
  }
}

const Sectie: React.FC<{ sectie: IntroSectie; children?: React.ReactNode }> = ({ sectie, children }) => (
  <section>
    <h3 className="font-semibold text-gray-900 flex items-start gap-1.5">
      <span aria-hidden="true">{sectie.emoji}</span>
      <span>{sectie.titel}</span>
    </h3>
    {sectie.alineas.map((a) => (
      <p key={a.slice(0, 40)} className="text-sm text-gray-700 mt-1.5 leading-relaxed">
        {a}
      </p>
    ))}
    {children}
  </section>
);

/**
 * "Veilige Haven"-introductie bovenaan het Medewerkersportaal. Bij het eerste
 * bezoek uitgeklapt; daarna ingeklapt tot de medewerker hem zelf opent.
 */
export const MedewerkerIntro: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const [open, setOpen] = useState(() => !isGelezen());
  const introQuery = useQuery({ queryKey: ['medewerker-intro'], queryFn: getIntro, staleTime: 10 * 60_000 });
  const intro = introQuery.data;
  if (!intro) return null;

  const wissel = () => {
    if (open) markeerGelezen();
    setOpen(!open);
  };

  return (
    <Card className="p-0 overflow-hidden">
      <button
        type="button"
        onClick={wissel}
        aria-expanded={open}
        aria-controls="medewerker-intro-inhoud"
        className="w-full flex items-center gap-3 p-4 text-left bg-blue-50/60 hover:bg-blue-50"
      >
        <span className="text-2xl leading-none" aria-hidden="true">
          🛡️
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-semibold text-gray-900">{t('intro.titel', { defaultValue: 'Welkom in jouw Veilige Haven' })}</span>
          <span className="block text-xs text-gray-600 mt-0.5">
            {t('intro.subtitel', { defaultValue: 'Hoe BeLagom werkt en hoe je privacy geregeld is' })}
          </span>
        </span>
        <ChevronDown className={`w-5 h-5 text-gray-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div id="medewerker-intro-inhoud" className="p-4 space-y-5">
          {intro.secties.map((s) => (
            <Sectie key={s.titel} sectie={s} />
          ))}

          <Sectie sectie={intro.experts}>
            {intro.experts.rollen.length > 0 && (
              <ul className="mt-3 space-y-2">
                {intro.experts.rollen.map((r) => (
                  <li key={r.code} className="rounded-lg border border-gray-200 p-3">
                    <p className="font-medium text-gray-900 text-sm">
                      <span aria-hidden="true">{r.emoji}</span> {r.titel}
                    </p>
                    <p className="text-sm text-gray-600 mt-0.5">{r.tekst}</p>
                  </li>
                ))}
              </ul>
            )}
            {intro.experts.overige_rollen.length > 0 && (
              <p className="text-sm text-gray-700 mt-2">
                {t('intro.overigeRollen', { defaultValue: 'Daarnaast:' })} {intro.experts.overige_rollen.join(', ')}
              </p>
            )}
            <p className="text-sm text-gray-700 mt-3 leading-relaxed">{intro.experts.slot}</p>
          </Sectie>

          <Sectie sectie={intro.hoe_werkt}>
            <ul className="mt-3 space-y-2.5">
              {intro.hoe_werkt.onderdelen.map((o) => (
                <li key={o.code} className="flex gap-2 text-sm">
                  <span aria-hidden="true">{o.emoji}</span>
                  <span className="text-gray-700">
                    <span className="font-medium text-gray-900">{o.titel}:</span> {o.tekst}
                  </span>
                </li>
              ))}
            </ul>
          </Sectie>

          <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900 flex gap-2">
            <LifeBuoy className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span>{intro.noodhulp}</span>
          </p>

          <p className="text-sm font-medium text-gray-900">{intro.afsluiting}</p>

          <button type="button" onClick={wissel} className="text-sm font-medium text-[#0B1F3A]">
            {t('intro.sluiten', { defaultValue: 'Gelezen, inklappen' })}
          </button>
        </div>
      )}
    </Card>
  );
};
